import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useGenerationPolling } from '@/hooks/useGenerationPolling';
import { PageHeader } from '@/components/layout/PageHeader';
import { MarketingStrategyCreationModal, MarketingStrategyFormData } from '@/components/marketing-strategy/MarketingStrategyCreationModal';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { SubscriptionLimitBanner } from '@/components/shared/SubscriptionLimitBanner';
import { AiItemsList } from '@/components/shared/AiItemsList';
import { AiDetailPanel } from '@/components/shared/AiDetailPanel';
import { useAiPagePermissions } from '@/hooks/useAiPagePermissions';
import { projectsApi, marketingStrategiesApi, marketAnalysesApi, personasApi, positioningsApi } from '@/utils/api-pb';
import type { Project, MarketingStrategy, MarketAnalysis, Persona, Positioning } from '@/utils/pocketbase';

import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/i18n/LanguageContext';
const FREE_LIMIT = 0;

export default function MarketingStrategyPage() {
  const { t } = useTranslation('marketing-strategy');
  const { lang } = useLanguage();
  const [searchParams] = useSearchParams();
  // Data state
  const [projects, setProjects] = useState<Project[]>([]);
  const [strategies, setStrategies] = useState<MarketingStrategy[]>([]);
  const [marketAnalyses, setMarketAnalyses] = useState<MarketAnalysis[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [positionings, setPositionings] = useState<Positioning[]>([]);
  const [selectedStrategy, setSelectedStrategy] = useState<MarketingStrategy | null>(null);

  // Form state
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [strategyToDelete, setStrategyToDelete] = useState<string | null>(null);
  const [editingStrategyId, setEditingStrategyId] = useState<string | null>(null);

  // Permissions
  const { isFree, isBlockedForBasic, canCreateNew, canDelete } = useAiPagePermissions(strategies.length, FREE_LIMIT, 'marketingStrategy');

  // Ref for old strategy ID (persists across async boundary)
  const oldStrategyIdRef = useRef<string | null>(null);

  // Polling for async generation
  const handleStrategyCompleted = useCallback(() => {
    setIsLoading(false);
    if (oldStrategyIdRef.current) {
      marketingStrategiesApi.delete(oldStrategyIdRef.current).catch(() => {});
      setEditingStrategyId(null);
      oldStrategyIdRef.current = null;
    }
    setShowNewModal(false);
    loadData();
    toast.success(t('toast.ready'));
  }, []);

  const { startPolling } = useGenerationPolling({
    collection: 'marketing_strategies',
    onCompleted: handleStrategyCompleted,
    onFailed: () => {
      setIsLoading(false);
      toast.error(t('toast.generateError'));
    },
    onTimeout: () => {
      setIsLoading(false);
      toast.error(t('toast.timeoutError'));
    },
  });

  // Tooltip state for create button
  const [tooltipVisible, setTooltipVisible] = useState<{ id: string; type: string } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  // Auto-select strategy (prefer recordId from query param, fallback to first)
  useEffect(() => {
    if (strategies.length > 0 && !selectedStrategy) {
      const targetId = searchParams.get('recordId');
      const target = targetId ? strategies.find(s => s.id === targetId) : null;
      setSelectedStrategy(target ?? strategies[0] ?? null);
    }
  }, [strategies, selectedStrategy, searchParams]);

  const loadData = async () => {
    try {
      setIsInitialLoading(true);
      const [projectsResult, strategiesResult, analysesResult, personasResult, positioningsResult] = await Promise.all([
        projectsApi.list(),
        marketingStrategiesApi.list().catch(() => [] as MarketingStrategy[]),
        marketAnalysesApi.list(),
        personasApi.list(),
        positioningsApi.list(),
      ]);
      setProjects(projectsResult);
      setStrategies(strategiesResult);
      setMarketAnalyses(analysesResult);
      setPersonas(personasResult);
      setPositionings(positioningsResult);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleSubmitModal = async (formData: MarketingStrategyFormData) => {
    setIsLoading(true);
    oldStrategyIdRef.current = editingStrategyId;

    try {
      const channelsStr = formData.currentChannels.join(', ');
      const result = await marketingStrategiesApi.generate(formData.project_id, {
        budget: formData.budget,
        mainGoal: formData.mainGoal,
        teamSize: formData.teamSize,
        currentChannels: channelsStr || undefined,
        marketAnalysisId: formData.useMarketAnalysis && formData.market_analysis_id ? formData.market_analysis_id : undefined,
        personaId: formData.usePersona && formData.persona_id ? formData.persona_id : undefined,
        positioningId: formData.usePositioning && formData.positioning_id ? formData.positioning_id : undefined,
        lang,
      });

      if (result.pending && result.strategy?.id) {
        // Backend returned 202: poll for completion
        startPolling(result.strategy.id);
        return;
      }

      // Non-pending response (direct completion)
      if (oldStrategyIdRef.current) {
        await marketingStrategiesApi.delete(oldStrategyIdRef.current).catch(() => {});
        setEditingStrategyId(null);
        oldStrategyIdRef.current = null;
      }

      setShowNewModal(false);
      setIsLoading(false);
      loadData();
    } catch (error) {
      console.error('Error creating marketing strategy:', error);
      const errorMessage = error instanceof Error ? error.message : t('toast.createError');
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await marketingStrategiesApi.delete(id);
      setShowDeleteConfirm(false);
      setStrategyToDelete(null);
      if (selectedStrategy?.id === id) {
        setSelectedStrategy(null);
      }
      loadData();
    } catch (error) {
      console.error('Error deleting marketing strategy:', error);
      toast.error(t('toast.deleteError'));
    }
  };

  const handleCreateNew = () => {
    if (!canCreateNew) return;
    setEditingStrategyId(null);
    setShowNewModal(true);
  };

  const handleEdit = (strategy: MarketingStrategy) => {
    setEditingStrategyId(strategy.id);
    setShowNewModal(true);
  };

  const handleSelectStrategy = (strategy: MarketingStrategy) => {
    setSelectedStrategy(strategy);
  };

  // Build initial form data from an existing strategy for re-generation
  const getInitialDataFromStrategy = (strategy: MarketingStrategy) => {
    const d = strategy.input_data;
    if (!d) return undefined;
    const channels = d.currentChannels
      ? d.currentChannels.split(', ').filter(Boolean)
      : [];
    return {
      project_id: strategy.project,
      budget: d.budget ?? '',
      mainGoal: d.mainGoal ?? '',
      teamSize: d.teamSize ?? '',
      currentChannels: channels,
      market_analysis_id: d.marketAnalysisId ?? '',
      persona_id: d.personaId ?? '',
      positioning_id: d.positioningId ?? '',
      useMarketAnalysis: !!d.marketAnalysisId,
      usePersona: !!d.personaId,
      usePositioning: !!d.positioningId,
    };
  };

  // Get strategy title based on product name
  const getStrategyTitle = (strategy: MarketingStrategy): string => {
    const project = projects.find(p => p.id === strategy.project);
    return t('itemTitle', { projectName: project?.name || t('defaultProjectName') });
  };

  // Count strategies per project for numbering (chronological: oldest = 1)
  const strategyNumbers = useMemo(() => {
    const projectCounts: Record<string, number> = {};
    const numbers: Record<string, { displayNumber: number; total: number }> = {};

    strategies.forEach(strategy => {
      const projectId = strategy.project || '';
      projectCounts[projectId] = (projectCounts[projectId] || 0) + 1;
    });

    const projectIndexes: Record<string, number> = {};
    strategies.forEach(strategy => {
      const projectId = strategy.project || '';
      projectIndexes[projectId] = (projectIndexes[projectId] || 0) + 1;
      numbers[strategy.id] = {
        displayNumber: projectIndexes[projectId] ?? 1,
        total: projectCounts[projectId] ?? 1,
      };
    });

    return numbers;
  }, [strategies]);

  const getStrategyListTitle = (strategy: MarketingStrategy): string => {
    const numberInfo = strategyNumbers[strategy.id];
    const shouldShowNumber = numberInfo && numberInfo.total > 1;
    const displayNum = numberInfo?.displayNumber ?? 1;
    return `${getStrategyTitle(strategy)}${shouldShowNumber ? ` ${displayNum}` : ''}`;
  };

  if (!isInitialLoading && (isFree || isBlockedForBasic)) {
    return (
      <><PageHeader title={t('title')} description={t('description')} />
        <SubscriptionLimitBanner
          title={isBlockedForBasic ? t('paidBanner.titlePremium') : t('paidBanner.title')}
          description={isBlockedForBasic ? t('paidBanner.descriptionPremium') : t('paidBanner.description')}
        />
      </>
    );
  }

  return (
    <>
      {isInitialLoading ? (
        <div className="flex flex-col items-center justify-center gap-[16px] py-[80px]">
          <h2 className="font-manrope font-bold text-[32px] leading-[40px] text-[#0d0d0d] text-center">
            {t('title')}
          </h2>
          <p className="font-segoe text-[16px] leading-[24px] text-[#565858] text-center">
            {t('loading')}
          </p>
          <div className="w-[32px] h-[32px] border-2 border-[#2197f1] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : strategies.length === 0 ? (
        <><PageHeader title={t('title')} description={t('description')} />
        <div className="flex flex-col gap-[24px]">
          <div className="font-segoe text-[16px] leading-[24px] text-[#565858] max-w-[769px]">
            <p>
              {t('empty.text')}
            </p>
          </div>

          <button
              onClick={handleCreateNew}
              disabled={!canCreateNew}
              onMouseEnter={() => !canCreateNew && setTooltipVisible({ id: 'create-new', type: 'create' })}
              onMouseLeave={() => setTooltipVisible(null)}
              className="relative inline-block"
            >
              <div className={`${canCreateNew
                ? 'bg-[#2197f1] hover:bg-[#1a7bc7]'
                : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed opacity-60'
                } rounded-[16px] px-[24px] py-[12px] transition-colors inline-block`}>
                <p className="font-segoe font-semibold text-[16px] leading-[24px] text-white text-center">
                  {t('newButton')}
                </p>
              </div>

              {tooltipVisible?.id === 'create-new' && !canCreateNew && (
                <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                  <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                    {t('tooltip.paidOnly')}
                  </p>
                </div>
              )}
            </button>
        </div>
        </>
      ) : (
        <div className="flex flex-col lg:flex-row gap-[24px]">
          {/* Left Sidebar - Strategies List */}
          <div className="flex flex-col gap-[8px] w-full lg:w-[424px] lg:flex-shrink-0 lg:sticky lg:top-[24px] lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto">
            <PageHeader title={t('title')} description={t('description')} />
            <AiItemsList
              items={strategies}
              selectedId={selectedStrategy?.id ?? null}
              getItemTitle={getStrategyListTitle}
              onSelect={handleSelectStrategy}
              isItemDisabled={(_item, index) => isFree && index > 0}
              tooltipMessage={t('tooltip.paidOnlyShort')}
            />

            {/* Create New Button */}
            <button
              onClick={handleCreateNew}
              disabled={!canCreateNew}
              onMouseEnter={() => !canCreateNew && setTooltipVisible({ id: 'create-new-sidebar', type: 'create' })}
              onMouseLeave={() => setTooltipVisible(null)}
              className="relative"
            >
              <div className={`${canCreateNew
                ? 'bg-[#2197f1] hover:bg-[#1a7bc7]'
                : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed opacity-60'
                } rounded-[16px] px-[24px] py-[12px] transition-colors`}>
                <p className="font-segoe font-semibold text-[16px] leading-[24px] text-white text-center">
                  {t('newButton')}
                </p>
              </div>

              {tooltipVisible?.id === 'create-new-sidebar' && !canCreateNew && (
                <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                  <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                    {t('tooltip.paidOnly')}
                  </p>
                </div>
              )}
            </button>

          </div>

          {/* Right Content - Selected Strategy */}
          {selectedStrategy && (
            <AiDetailPanel
              key={selectedStrategy.id}
              title={getStrategyTitle(selectedStrategy)}
              content={selectedStrategy.generated_text}
              canDelete={canDelete}
              onDelete={() => {
                setStrategyToDelete(selectedStrategy.id);
                setShowDeleteConfirm(true);
              }}
              canEdit={canDelete}
              onEdit={() => handleEdit(selectedStrategy)}
              editButtonText={t('detail.editButton')}
              pdfExportData={selectedStrategy.generated_text ? {
                title: getStrategyTitle(selectedStrategy),
                content: selectedStrategy.generated_text,
                artifactType: 'marketing_strategy',
                artifactTypeLabel: t('detail.pdfLabel'),
                projectName: projects.find(p => p.id === selectedStrategy.project)?.name || t('defaultProjectName'),
                date: selectedStrategy.created,
              } : undefined}
              tooltipEditText={t('tooltip.paidOnlyShort')}
              tooltipDeleteText={t('tooltip.paidOnlyShort')}
              emptyText={t('detail.emptyText')}
            />
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setStrategyToDelete(null);
        }}
        onConfirm={() => strategyToDelete && handleDelete(strategyToDelete)}
        title={t('confirm.deleteTitle')}
        description={t('confirm.deleteDescription')}
        confirmText={t('confirm.deleteConfirm')}
        cancelText={t('confirm.deleteCancel')}
      />

      {/* Marketing Strategy Creation / Edit Modal */}
      <MarketingStrategyCreationModal
        isOpen={showNewModal}
        onClose={() => { setShowNewModal(false); setEditingStrategyId(null); }}
        onSubmit={handleSubmitModal}
        projects={projects}
        marketAnalyses={marketAnalyses}
        personas={personas}
        positionings={positionings}
        isLoading={isLoading}
        initialData={
          editingStrategyId
            ? getInitialDataFromStrategy(strategies.find(s => s.id === editingStrategyId)!)
            : undefined
        }
      />
    </>
  );
}
